import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Receipt, ClipboardCheck, TrendingUp, FileText, ArrowLeft, Wallet,
  AlertTriangle, CalendarClock, BarChart3, HardHat, Truck,
} from "lucide-react";
import api, { fmtMXN, fmtDate, PROJECT_STATUS } from "@/lib/api";
import { StatusBadge } from "@/components/PageHeader";
import BitacoraPanel from "@/components/BitacoraPanel";
import ProjectTabs from "@/components/ProjectTabs";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const BUCKET_LABEL = {
  vencido: { label: "Vencidos", color: "#ef4444" },
  esta_semana: { label: "Esta semana", color: "#fbbf24" },
  este_mes: { label: "Este mes", color: "#22d3ee" },
};

export default function ProjectDashboard() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openBitacora, setOpenBitacora] = useState(false);

  useEffect(() => {
    api.get(`/projects/${id}/dashboard`)
      .then(({ data }) => setData(data))
      .catch(() => toast.error("No se pudo cargar el panel del proyecto"))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="p-10 text-center text-slate-400">Cargando…</div>;
  if (!data) return <div className="p-10 text-center text-slate-400">Proyecto no encontrado.</div>;

  const { project: p, kpis: k, upcoming } = data;

  return (
    <div data-testid="project-dashboard" className="px-6 lg:px-8 py-6 space-y-5">
      {/* Header */}
      <div>
        <Link to="/projects" data-testid="back-to-projects" className="text-xs text-slate-400 hover:text-cyan-300 inline-flex items-center gap-1 mb-2">
          <ArrowLeft className="w-3.5 h-3.5" /> Proyectos
        </Link>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="label-mono" style={{ color: "var(--cyan)" }}>{p.code}</div>
            <h1 className="font-display font-bold text-2xl lg:text-3xl text-slate-100 mt-0.5">{p.name}</h1>
            <div className="flex items-center gap-3 mt-2 flex-wrap text-xs text-slate-400">
              <StatusBadge items={PROJECT_STATUS} value={p.status} />
              {p.client && <span>{p.client}</span>}
              {p.start_date && <span>{fmtDate(p.start_date)} → {fmtDate(p.end_date)}</span>}
              <span className="flex items-center gap-1.5">
                <span className="w-16 h-1 rounded-sm overflow-hidden inline-block" style={{ background: "var(--bg-2)" }}>
                  <span className="h-full block" style={{ width: `${p.progress || 0}%`, background: "var(--cyan)" }} />
                </span>
                <span className="font-mono">{p.progress || 0}%</span>
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {["admin", "contador"].includes(user?.role) && (
              <a href={`/projects/${p.id}/report`} target="_blank" rel="noreferrer" data-testid="link-report" className="btn-ghost px-3 py-2 rounded-sm text-[10px] uppercase tracking-widest flex items-center gap-1.5" style={{ color: "var(--magenta)" }}>
                <BarChart3 className="w-3.5 h-3.5" /> Reporte
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Pestañas del proyecto */}
      <ProjectTabs projectId={p.id} active="resumen" />

      {/* Acciones rápidas */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="project-quick-actions">
        <QuickAction to={`/projects/${p.id}/expenses`} testId="qa-expense" icon={Receipt} label="Capturar gasto"
          color="34,211,238" iconColor="var(--cyan)" />
        <QuickAction to="/payroll?tab=workdays" testId="qa-attendance" icon={ClipboardCheck} label="Días trabajados"
          color="99,102,241" iconColor="#a5b4fc" />
        <QuickAction to={`/projects/${p.id}/progress-mobile`} testId="qa-progress" icon={TrendingUp} label="Capturar avance"
          color="22,163,74" iconColor="#4ade80" />
        <QuickAction onClick={() => setOpenBitacora(true)} testId="qa-bitacora" icon={FileText} label="Bitácora de obra"
          color="217,70,239" iconColor="var(--magenta)" />
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="project-kpis">
        <Kpi label="Presupuesto total" value={fmtMXN(k.budget)} icon={HardHat} color="var(--cyan)" />
        <Kpi
          label="Gastos ejecutados"
          value={fmtMXN(k.gastos_ejecutados)}
          sub={k.pct_ejecutado != null ? `${k.pct_ejecutado}% del presupuesto` : null}
          icon={Receipt}
          color={k.pct_ejecutado != null && k.pct_ejecutado > 100 ? "#ef4444" : "var(--magenta)"}
        />
        <Kpi label="Nómina pagada" value={fmtMXN(k.nomina_pagada)} sub={k.nomina_borrador > 0 ? `${fmtMXN(k.nomina_borrador)} en borrador` : null} icon={Wallet} color="#4ade80" />
        <Kpi label="Gasto maquinaria" value={fmtMXN(k.maquinaria_gastado || 0)} sub="cortes registrados" icon={Truck} color="#a78bfa" />
        <Kpi label="Pagos por vencer" value={fmtMXN(k.por_vencer_total)} sub={`${k.por_vencer_count} pago${k.por_vencer_count === 1 ? "" : "s"} sin liquidar`} icon={CalendarClock} color="#fbbf24" />
      </div>

      {/* Destajos vs Nómina */}
      {(k.destajos_pagado > 0 || k.nomina_pagada > 0) && (() => {
        const laborTotal = (k.destajos_pagado || 0) + (k.nomina_pagada || 0);
        const pctD = laborTotal > 0 ? ((k.destajos_pagado || 0) / laborTotal) * 100 : 0;
        const pctN = laborTotal > 0 ? ((k.nomina_pagada || 0) / laborTotal) * 100 : 0;
        return (
          <div className="panel p-4" data-testid="labor-split-panel">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="label-mono">// MANO DE OBRA · DESTAJOS VS NÓMINA</div>
              <div className="text-xs text-slate-400">Total: <span className="font-mono font-bold text-slate-100">{fmtMXN(laborTotal)}</span></div>
            </div>
            <div className="space-y-2.5">
              <div data-testid="labor-destajos-row">
                <div className="flex items-center justify-between mb-1 text-xs">
                  <span className="text-slate-300 font-semibold uppercase tracking-wider text-[10px]">Destajos</span>
                  <span className="font-mono font-bold" style={{ color: "var(--magenta)" }}>{fmtMXN(k.destajos_pagado || 0)} <span className="text-slate-500 font-normal">({pctD.toFixed(0)}%)</span></span>
                </div>
                <div className="h-2 rounded-sm" style={{ background: "var(--bg-2)" }}>
                  <div className="h-full rounded-sm" style={{ width: `${pctD}%`, background: "var(--magenta)" }} />
                </div>
              </div>
              <div data-testid="labor-nomina-row">
                <div className="flex items-center justify-between mb-1 text-xs">
                  <span className="text-slate-300 font-semibold uppercase tracking-wider text-[10px]">Nómina pagada</span>
                  <span className="font-mono font-bold" style={{ color: "#4ade80" }}>{fmtMXN(k.nomina_pagada || 0)} <span className="text-slate-500 font-normal">({pctN.toFixed(0)}%)</span></span>
                </div>
                <div className="h-2 rounded-sm" style={{ background: "var(--bg-2)" }}>
                  <div className="h-full rounded-sm" style={{ width: `${pctN}%`, background: "#4ade80" }} />
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Pagos próximos por vencer */}
      <div className="panel p-4" data-testid="upcoming-payments-panel">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="label-mono">// PAGOS PRÓXIMOS POR VENCER</div>
          <div className="flex items-center gap-1.5">
            {Object.entries(BUCKET_LABEL).map(([key, b]) => (
              (k.por_vencer_buckets?.[key] || 0) > 0 && (
                <span key={key} className="px-2 py-0.5 rounded-sm text-[10px] font-semibold font-mono"
                  style={{ background: `${b.color}18`, color: b.color, border: `1px solid ${b.color}44` }}>
                  {b.label}: {fmtMXN(k.por_vencer_buckets[key])}
                </span>
              )
            ))}
          </div>
        </div>
        {upcoming.length === 0 ? (
          <div className="text-center text-slate-500 text-xs py-6" data-testid="no-upcoming">
            Sin pagos pendientes. Todos los gastos de este proyecto están liquidados. ✅
          </div>
        ) : (
          <div className="space-y-1.5">
            {upcoming.map((u) => {
              const b = BUCKET_LABEL[u.bucket] || { label: "Próximo", color: "#94a3b8" };
              return (
                <div key={u.id} data-testid={`upcoming-${u.id}`} className="flex items-center gap-3 p-2 rounded-sm text-xs flex-wrap" style={{ background: "rgba(15,23,42,0.5)", border: "1px solid var(--border)" }}>
                  <span className="px-1.5 py-0.5 rounded-sm text-[9px] uppercase font-bold shrink-0" style={{ background: `${b.color}18`, color: b.color, border: `1px solid ${b.color}44` }}>
                    {u.bucket === "vencido" && <AlertTriangle className="w-3 h-3 inline mr-0.5 -mt-0.5" />}
                    {b.label}
                  </span>
                  <span className="font-mono text-slate-400 shrink-0">vence {fmtDate(u.due_date)}</span>
                  <span className="text-slate-200 flex-1 min-w-[140px] truncate">{u.vendor ? `${u.vendor} · ` : ""}{u.description}</span>
                  <span className="text-[10px] uppercase text-slate-500">{u.status}</span>
                  <span className="font-mono font-bold text-slate-100 tabular-nums">{fmtMXN(u.amount)}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {openBitacora && (
        <BitacoraPanel
          projectId={p.id}
          projectCode={p.code}
          currentUser={user}
          onClose={() => setOpenBitacora(false)}
        />
      )}
    </div>
  );
}

function QuickAction({ to, onClick, testId, icon: Icon, label, color, iconColor }) {
  const cls = "panel-strong relative overflow-hidden p-4 flex flex-col items-center text-center gap-2 transition-transform active:scale-95 cursor-pointer w-full";
  const style = { borderColor: `rgba(${color},0.45)`, background: `linear-gradient(135deg, rgba(${color},0.16), rgba(${color},0.03))` };
  const inner = (
    <>
      <div className="w-11 h-11 rounded-sm flex items-center justify-center" style={{ background: `rgba(${color},0.18)`, color: iconColor, border: `1px solid rgba(${color},0.4)` }}>
        <Icon className="w-5 h-5" />
      </div>
      <span className="text-[11px] font-bold uppercase tracking-wider text-slate-100">{label}</span>
    </>
  );
  if (onClick) return <button type="button" onClick={onClick} data-testid={testId} className={cls} style={style}>{inner}</button>;
  return <Link to={to} data-testid={testId} className={cls} style={style}>{inner}</Link>;
}

function Kpi({ label, value, sub, icon: Icon, color }) {
  return (
    <div className="panel p-3.5">
      <div className="flex items-center justify-between">
        <div className="label-mono text-[10px]">{label}</div>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <div className="font-mono font-bold text-xl tabular-nums mt-1.5" style={{ color }}>{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}
